import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin } from "lucide-react";
import client from "@/lib/api";
import { LANGS, LANG_LABELS, loc } from "@/i18n";

export function usePublicSettings() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    client
      .get("/settings/public")
      .then((r) => {
        setSettings(r.data);
        const accent = r.data?.theme?.accent;
        if (accent) document.documentElement.style.setProperty("--ci-accent", accent);
      })
      .catch(() => setSettings({ site_name: "CarInspecto", theme: {} }));
  }, []);
  return settings;
}

function LangSwitcher() {
  const { i18n } = useTranslation();
  const change = (l) => {
    i18n.changeLanguage(l);
    localStorage.setItem("ci_lang", l);
  };
  return (
    <div className="flex items-center gap-1" data-testid="lang-switcher">
      {LANGS.map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => change(l)}
          className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
            i18n.language === l ? "text-black" : "text-neutral-400 hover:text-neutral-700"
          }`}
          style={i18n.language === l ? { background: "var(--ci-accent)" } : {}}
        >
          {LANG_LABELS[l]}
        </button>
      ))}
    </div>
  );
}

export default function PublicShell({ children, settings }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const siteName = settings?.site_name || "CarInspecto";
  const f = settings?.footer || {};
  const showFooter = f.show_footer !== false;
  const note = loc(f.company_note, lang);
  const address = loc(f.address, lang);
  const hasContacts = f.email || f.phone || address || note;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto max-w-3xl px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-home-link">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "var(--ci-accent)" }} />
            <span className="text-lg font-semibold tracking-tight">{siteName}</span>
          </Link>
          <div className="flex items-center gap-4 sm:gap-6">
            <nav className="hidden sm:flex items-center gap-6 text-sm">
              <Link to="/" className="text-neutral-600 hover:text-black transition-colors" data-testid="nav-verify">{t("nav.verify")}</Link>
              <Link to="/decoder" className="text-neutral-600 hover:text-black transition-colors" data-testid="nav-decoder">{t("nav.decoder")}</Link>
            </nav>
            <LangSwitcher />
          </div>
        </div>
        <div className="h-[3px]" style={{ background: "var(--ci-accent)" }} />
      </header>
      <main className="flex-1">{children}</main>

      {showFooter && (
        <footer className="border-t border-neutral-200 bg-neutral-50" data-testid="site-footer">
          <div className="mx-auto max-w-3xl px-6 py-10">
            <div className="flex items-center gap-2 mb-5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "var(--ci-accent)" }} />
              <span className="font-semibold tracking-tight">{siteName}</span>
            </div>

            {hasContacts && (
              <>
                <div className="text-xs uppercase tracking-widest text-neutral-400 mb-3">{t("footer.contacts")}</div>
                <div className="grid gap-3 sm:grid-cols-2 mb-6">
                  {f.email && (
                    <a href={`mailto:${f.email}`} className="flex items-center gap-2.5 text-sm text-neutral-700 hover:text-black transition-colors" data-testid="footer-email">
                      <Mail className="w-4 h-4 shrink-0" style={{ color: "var(--ci-accent)" }} />{f.email}
                    </a>
                  )}
                  {f.phone && (
                    <a href={`tel:${f.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2.5 text-sm text-neutral-700 hover:text-black transition-colors" data-testid="footer-phone">
                      <Phone className="w-4 h-4 shrink-0" style={{ color: "var(--ci-accent)" }} />{f.phone}
                    </a>
                  )}
                  {address && (
                    <div className="flex items-start gap-2.5 text-sm text-neutral-700 sm:col-span-2" data-testid="footer-address">
                      <MapPin className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--ci-accent)" }} />
                      <span className="whitespace-pre-line">{address}</span>
                    </div>
                  )}
                </div>
                {note && <p className="text-sm text-neutral-600 whitespace-pre-line mb-6 max-w-xl" data-testid="footer-note">{note}</p>}
              </>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-neutral-200 text-xs text-neutral-400">
              <span>© {new Date().getFullYear()} {siteName}</span>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
